import { effect, inject, Injectable, signal } from '@angular/core';
import { StreamCredit } from 'src/app/interfaces/api/project';
import { ProjectService } from './project.service';

/**
 * Per-broadcaster daily stream-credit coordinator. Each broadcaster (camera) has
 * its own daily allowance (streamDailyLimit) and its own usage. While a widget is
 * watching a broadcaster, this service heartbeats presence to the backend, which
 * accumulates that camera's watched time deduped across all viewers. Widgets read
 * the credit for their own broadcaster to display usage and enforce its limit.
 */
@Injectable({ providedIn: 'root' })
export class StreamCreditService {
  private readonly projectService = inject(ProjectService);
  private readonly viewerId = crypto.randomUUID();

  private readonly _total = signal(0);
  private readonly _played = signal<Record<string, number>>({});       // server value per broadcaster
  private readonly _localSeconds = signal<Record<string, number>>({}); // local accrual since last sync
  private readonly _hasCredit = signal(false);
  private _date: string | number | null = null;

  private readonly watcherCountByBroadcaster = new Map<string, number>(); // broadcaster -> watching widget count
  private ticker?: ReturnType<typeof setInterval>;
  private secondsSinceFlush = 0;
  private readonly FLUSH_EVERY = 5;

  get total(): number { return this._total(); }

  // Today's credit/limit has been fetched from the backend (total is authoritative).
  get limitKnown(): boolean { return this._hasCredit(); }

  livePlayedFor(broadcasterId: string): number {
    return (this._played()[broadcasterId] || 0) + (this._localSeconds()[broadcasterId] || 0);
  }

  limitReachedFor(broadcasterId: string): boolean {
    const total = this._total();
    return total > 0 && this.livePlayedFor(broadcasterId) >= total;
  }

  creditFor(broadcasterId: string): StreamCredit | null {
    if (!this._hasCredit()) return null;
    return { date: this._date!, broadcaster: broadcasterId, played: this.livePlayedFor(broadcasterId), total: this._total() };
  }

  constructor() {
    effect(() => {
      const project = this.projectService.currentProjectSignal();
      if (project?.id) {
        this.refresh();
      } else {
        this._hasCredit.set(false);
        this._total.set(0);
        this._played.set({});
        this._localSeconds.set({});
      }
    });
  }

  startConsuming(broadcasterId: string): void {
    this.watcherCountByBroadcaster.set(broadcasterId, (this.watcherCountByBroadcaster.get(broadcasterId) || 0) + 1);
    if (!this.ticker) this.ticker = setInterval(() => this.tick(), 1000);
    this.report(broadcasterId);
  }

  stopConsuming(broadcasterId: string): void {
    const remaining = (this.watcherCountByBroadcaster.get(broadcasterId) || 0) - 1;
    if (remaining <= 0) {
      this.watcherCountByBroadcaster.delete(broadcasterId);
      this.report(broadcasterId, true); // leave presence so the backend stops billing immediately
    } else {
      this.watcherCountByBroadcaster.set(broadcasterId, remaining);
    }
    if (this.watcherCountByBroadcaster.size === 0 && this.ticker) {
      clearInterval(this.ticker);
      this.ticker = undefined;
    }
  }

  refresh(): void {
    this.report();
  }

  private tick(): void {
    if (this.watcherCountByBroadcaster.size === 0) return;
    this._localSeconds.update(m => {
      const next = { ...m };
      for (const b of this.watcherCountByBroadcaster.keys()) next[b] = (next[b] || 0) + 1;
      return next;
    });
    if (++this.secondsSinceFlush >= this.FLUSH_EVERY) {
      this.secondsSinceFlush = 0;
      for (const b of this.watcherCountByBroadcaster.keys()) this.report(b);
    }
  }

  private report(broadcasterId?: string, stop = false): void {
    const project = this.projectService.currentProjectValue;
    if (!project?.id) return;

    this.projectService.reportStreamUsage(project.id, project.database, broadcasterId, this.viewerId, stop).subscribe({
      next: (res: any) => {
        if (!res) return;
        this._hasCredit.set(true);
        this._date = res.date;
        this._total.set(res.total || 0);

        if (broadcasterId) {
          this._played.update(m => ({ ...m, [broadcasterId]: res.played || 0 }));
          this._localSeconds.update(m => ({ ...m, [broadcasterId]: 0 }));
        } else if (Array.isArray(res.credits)) {
          const map: Record<string, number> = {};
          for (const c of res.credits) map[c.broadcaster] = c.played || 0;
          this._played.set(map);
        }
      },
      error: () => {}
    });
  }
}
